export const INTERCOM_VERTICAL_PADDING = 80;

export const INTERCOM_PADDING_STYLE_ID = "intercom-mobile-vertical-padding";

const INTERCOM_MESSENGER_OPEN_ATTR = "data-intercom-messenger-open";

const INTERCOM_LAUNCHER_PADDING_DATA_ATTR = "data-prodigio-intercom-launcher-padding";

const INTERCOM_LIGHTWEIGHT_LAUNCHER_SELECTORS = [".intercom-lightweight-app-launcher", ".intercom-lightweight-app .intercom-launcher"];

const INTERCOM_MESSENGER_SELECTORS = [
    ".intercom-lightweight-app-messenger",
    ".intercom-messenger-frame",
    ".intercom-messenger",
    ".intercom-borderless-messenger"
];

const INTERCOM_ROOT_SELECTORS = [".intercom-namespace", ".intercom-app", ".intercom-lightweight-app"];

let isIntercomMessengerOpenFromSdk = false;

const intercomLauncherPaddingSelector = `html:not([${INTERCOM_MESSENGER_OPEN_ATTR}="true"])`;

export const intercomLauncherPaddingCss = `
    ${intercomLauncherPaddingSelector} ${INTERCOM_LIGHTWEIGHT_LAUNCHER_SELECTORS.join(`,\n    ${intercomLauncherPaddingSelector} `)},
    ${intercomLauncherPaddingSelector} .intercom-app > div:has(iframe.intercom-launcher-frame),
    ${intercomLauncherPaddingSelector} .intercom-lightweight-app > div:has(iframe.intercom-launcher-frame) {
        bottom: ${INTERCOM_VERTICAL_PADDING}px !important;
    }
`;

const ensureLauncherPaddingStyleElement = () => {
    if (document.getElementById(INTERCOM_PADDING_STYLE_ID)) {
        return;
    }

    const styleElement = document.createElement("style");
    styleElement.id = INTERCOM_PADDING_STYLE_ID;
    styleElement.textContent = intercomLauncherPaddingCss;
    document.head.appendChild(styleElement);
};

const removeLauncherPaddingStyleElement = () => {
    document.getElementById(INTERCOM_PADDING_STYLE_ID)?.remove();
};

const applyPaddingToElement = (node: HTMLElement) => {
    node.setAttribute(INTERCOM_LAUNCHER_PADDING_DATA_ATTR, "true");
    node.style.setProperty("bottom", `${INTERCOM_VERTICAL_PADDING}px`, "important");
};

const clearPaddingFromElement = (node: HTMLElement) => {
    if (node.getAttribute(INTERCOM_LAUNCHER_PADDING_DATA_ATTR) !== "true") {
        return;
    }

    node.style.removeProperty("bottom");
    node.removeAttribute(INTERCOM_LAUNCHER_PADDING_DATA_ATTR);
};

export const clearIntercomLauncherPadding = () => {
    document.querySelectorAll(`[${INTERCOM_LAUNCHER_PADDING_DATA_ATTR}="true"]`).forEach((node) => {
        if (node instanceof HTMLElement) {
            clearPaddingFromElement(node);
        }
    });
};

const resetIntercomLauncherPaddingState = () => {
    isIntercomMessengerOpenFromSdk = false;
    clearIntercomLauncherPadding();
    document.documentElement.removeAttribute(INTERCOM_MESSENGER_OPEN_ATTR);
    removeLauncherPaddingStyleElement();
};

export const setIntercomMessengerOpenFromSdk = (isOpen: boolean) => {
    isIntercomMessengerOpenFromSdk = isOpen;

    if (isOpen) {
        document.documentElement.setAttribute(INTERCOM_MESSENGER_OPEN_ATTR, "true");
        clearIntercomLauncherPadding();
        return;
    }

    document.documentElement.removeAttribute(INTERCOM_MESSENGER_OPEN_ATTR);
    scheduleIntercomLauncherPaddingBurst();
};

const isMessengerElementVisible = (messenger: HTMLElement) => {
    const style = window.getComputedStyle(messenger);

    if (style.display === "none" || style.visibility === "hidden" || Number(style.opacity) === 0) {
        return false;
    }

    if (messenger.getAttribute("aria-hidden") === "true") {
        return false;
    }

    const { height, width, top } = messenger.getBoundingClientRect();

    return height > 80 && width > 80 && top < window.innerHeight - 80;
};

const isMessengerDomOpen = () => {
    return INTERCOM_MESSENGER_SELECTORS.some((selector) => {
        const messenger = document.querySelector(selector);

        return messenger instanceof HTMLElement && isMessengerElementVisible(messenger);
    });
};

const isIntercomMessengerVisible = () => isIntercomMessengerOpenFromSdk || isMessengerDomOpen();

const patchIntercomLauncherFrameHosts = () => {
    document.querySelectorAll("iframe.intercom-launcher-frame").forEach((iframe) => {
        let node: HTMLElement | null = iframe.parentElement;

        while (node) {
            const currentNode = node;

            if (INTERCOM_ROOT_SELECTORS.some((selector) => currentNode.matches(selector))) {
                break;
            }

            const style = window.getComputedStyle(currentNode);

            if (style.position === "fixed" || style.position === "absolute") {
                applyPaddingToElement(currentNode);
                break;
            }

            node = currentNode.parentElement;
        }
    });
};

const patchIntercomFixedLauncherElements = (root: HTMLElement) => {
    root.querySelectorAll<HTMLElement>("*").forEach((element) => {
        const style = window.getComputedStyle(element);
        const position = style.position;

        if (position !== "fixed" && position !== "absolute") {
            return;
        }

        const { width, height, bottom: elementBottom } = element.getBoundingClientRect();

        if (width === 0 || height === 0 || width > 96 || height > 96) {
            return;
        }

        if (elementBottom <= window.innerHeight - INTERCOM_VERTICAL_PADDING + 10) {
            return;
        }

        applyPaddingToElement(element);
    });
};

const applyIntercomLauncherPadding = () => {
    INTERCOM_LIGHTWEIGHT_LAUNCHER_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
            if (node instanceof HTMLElement) {
                applyPaddingToElement(node);
            }
        });
    });

    patchIntercomLauncherFrameHosts();

    INTERCOM_ROOT_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
            if (node instanceof HTMLElement) {
                patchIntercomFixedLauncherElements(node);
            }
        });
    });
};

export const syncIntercomLauncherPadding = () => {
    ensureLauncherPaddingStyleElement();

    if (isIntercomMessengerVisible()) {
        document.documentElement.setAttribute(INTERCOM_MESSENGER_OPEN_ATTR, "true");
        clearIntercomLauncherPadding();

        return;
    }

    document.documentElement.removeAttribute(INTERCOM_MESSENGER_OPEN_ATTR);
    applyIntercomLauncherPadding();
};

export const scheduleIntercomLauncherPadding = () => {
    requestAnimationFrame(() => {
        syncIntercomLauncherPadding();
        requestAnimationFrame(syncIntercomLauncherPadding);
    });
};

export const scheduleIntercomLauncherPaddingBurst = () => {
    [0, 50, 150, 350, 700, 1200].forEach((delay) => {
        window.setTimeout(scheduleIntercomLauncherPadding, delay);
    });
};

export const startIntercomLauncherPaddingSync = () => {
    let debounceTimer: number | undefined;
    let observedRoot: Element | null = null;
    let rootObserver: MutationObserver | null = null;
    let bodyObserver: MutationObserver | null = null;
    let mountPoll: number | undefined;
    let safetyPoll: number | undefined;

    const schedule = () => {
        if (debounceTimer) {
            window.clearTimeout(debounceTimer);
        }

        debounceTimer = window.setTimeout(scheduleIntercomLauncherPadding, 0);
    };

    const getIntercomRoot = () => document.querySelector(".intercom-namespace") || document.querySelector(".intercom-lightweight-app");

    const attachRootObserver = () => {
        const root = getIntercomRoot();

        if (!root) {
            observedRoot = null;
            rootObserver?.disconnect();
            rootObserver = null;

            return false;
        }

        if (observedRoot !== root) {
            rootObserver?.disconnect();
            observedRoot = root;
            rootObserver = new MutationObserver(schedule);
            rootObserver.observe(root, {
                attributes: true,
                attributeFilter: ["style", "class"],
                childList: true,
                subtree: true
            });
        }

        schedule();

        return true;
    };

    bodyObserver = new MutationObserver(() => {
        attachRootObserver();
        schedule();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    mountPoll = window.setInterval(() => {
        attachRootObserver();
    }, 300);

    safetyPoll = window.setInterval(() => {
        if (!getIntercomRoot()) {
            return;
        }

        scheduleIntercomLauncherPadding();
    }, 2000);

    window.addEventListener("resize", schedule);
    ensureLauncherPaddingStyleElement();
    attachRootObserver();
    schedule();

    return () => {
        bodyObserver?.disconnect();
        rootObserver?.disconnect();
        window.removeEventListener("resize", schedule);

        if (mountPoll) {
            window.clearInterval(mountPoll);
        }

        if (safetyPoll) {
            window.clearInterval(safetyPoll);
        }

        if (debounceTimer) {
            window.clearTimeout(debounceTimer);
        }

        resetIntercomLauncherPaddingState();
    };
};
